import React from 'react';

import {
  Flexbox,
  FontAwesomeIcon,
  Text,
  WithPopover,
  Avatar,
} from '@schoolinks/ui-components';
import { bool, func, shape } from 'prop-types';

import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';

import { faArrowRightFromBracket } from '@fortawesome/pro-regular-svg-icons/faArrowRightFromBracket';
import { faBug } from '@fortawesome/pro-regular-svg-icons/faBug';
import { faCircleUser } from '@fortawesome/pro-regular-svg-icons/faCircleUser';
import { faGear } from '@fortawesome/pro-regular-svg-icons/faGear';
import { faLanguage } from '@fortawesome/pro-regular-svg-icons/faLanguage';
import { faPerson } from '@fortawesome/pro-regular-svg-icons/faPerson';
import { faSitemap } from '@fortawesome/pro-regular-svg-icons/faSitemap';

import { faUsers } from '@fortawesome/pro-regular-svg-icons/faUsers';

import {
  DEFAULT_LANGUAGE_ENGLISH,
  I18N_TRANSLATE_COOKIE_KEY,
  GOOGLE_TRANSLATE_COOKIE_KEY,
  DEFAULT_ENGLISH_GOOGLE_VALUE,
} from 'components/Common/GoogleTranslateV2/constants';
import { clearGoogleTranslateCookies } from 'components/Common/GoogleTranslateV2/utils';

import { NAV_MENU_ICON_PROPS } from 'components/Common/LayoutV2/constants';
import Api from 'core/api';
import features from 'core/features';
import useLocalStorage from 'hooks/useLocalStorage';
import useMediaInfo from 'hooks/useMediaInfo';
import useModal from 'hooks/useModal';
import useMutation from 'hooks/useMutation';
import {
  ACCOUNT_SETTINGS_ACCOUNT_ACCESS,
  ACCOUNT_SETTINGS_AFFILIATIONS,
  SITEMAP,
} from 'routes/constants';
import { conditionalArray } from 'utils/arrays';
import getFullName from 'utils/getFullName';

import { ensureObject } from 'utils/objects';

import s from './Settings.css';

const shouldShowGoogleTranslate = (reduxState, cookieState) =>
  !!reduxState || !!cookieState;

const Settings = ({
  showGoogleTranslate,
  toggleGoogleTranslate,
  user: {
    isCounselor,
    isHESchoolAffiliation,
    isMember,
    isDemoStudent,
    districtSettings,
    firstName,
    lastName,
    profilePic,
    preferredName,
    email,
  },
  isOnboarding,
}) => {
  const { isDesktop } = useMediaInfo();
  const { i18n } = useTranslation();
  // eslint-disable-next-line no-unused-vars
  const [cookies, setCookie, removeCookie] = useCookies();
  const showGoogleTranslateDropdown = shouldShowGoogleTranslate(
    showGoogleTranslate,
    cookies[GOOGLE_TRANSLATE_COOKIE_KEY],
  );
  const { mutateAsync: setUserLanguage } = useMutation({
    apiFunc: Api.slUsers.updateUserDetails,
  });

  const { enableAccessibilityWidget } = ensureObject(districtSettings);
  const showAccessibilityWidgetMenuItem =
    isCounselor && enableAccessibilityWidget;

  const fullName = getFullName({ firstName, lastName, preferredName });

  const [showAccessibilityWidget, setShowAccessibilityWidget] = useLocalStorage(
    'showAccessibilityWidget',
    false,
  );

  const { showModal } = useModal();

  const handleAccessibilityWidgetItemClick = () => {
    // If we want to hide the accessibility widget, refresh the page so the widget goes away
    if (showAccessibilityWidget) {
      window.location.reload();
    }
    setShowAccessibilityWidget(!showAccessibilityWidget);
  };

  const handleToggleLanguageTranslation = async () => {
    toggleGoogleTranslate(!showGoogleTranslate);

    // Only clear out cookies on desktop. Mobile/tablet handle this via language selection drawer
    if (!isDesktop) {
      return;
    }

    if (showGoogleTranslateDropdown) {
      clearGoogleTranslateCookies({ removeCookie });

      await i18n.changeLanguage(DEFAULT_LANGUAGE_ENGLISH);
      await setUserLanguage({
        payload: {
          activeLanguage: DEFAULT_LANGUAGE_ENGLISH,
        },
      });
    } else {
      await i18n.changeLanguage(DEFAULT_LANGUAGE_ENGLISH);
      await setUserLanguage({
        payload: {
          activeLanguage: DEFAULT_LANGUAGE_ENGLISH,
        },
      });
    }
    const isI18NLanguageSelected =
      cookies[I18N_TRANSLATE_COOKIE_KEY] &&
      cookies[I18N_TRANSLATE_COOKIE_KEY] !== DEFAULT_LANGUAGE_ENGLISH;
    const isGoogleLanguageSelected =
      cookies[GOOGLE_TRANSLATE_COOKIE_KEY] &&
      cookies[GOOGLE_TRANSLATE_COOKIE_KEY] !== DEFAULT_ENGLISH_GOOGLE_VALUE;
    if (isI18NLanguageSelected || isGoogleLanguageSelected) {
      window.location.reload();
    }
  };

  return (
    <WithPopover
      aria-label="More Settings"
      tooltipProps={{
        title: 'More Settings',
        placement: 'bottom',
      }}
      paperClass={s.menuPaper}
      anchorButtonClass={s.anchorButton}
      anchorContent={() => (
        <FontAwesomeIcon
          data-testid="settings-icon"
          icon={faGear}
          {...NAV_MENU_ICON_PROPS}
        />
      )}
      menuProps={{
        items: [
          {
            disabled: true,
            textContent: () => (
              <Flexbox
                p={2}
                pt={1}
                columnGap={1}
                alignItems="center"
                className={s.userInfo}
              >
                {firstName && lastName ? (
                  <Avatar size="sm" src={profilePic}>
                    {`${firstName[0]}${lastName[0]}`}
                  </Avatar>
                ) : (
                  <Avatar size="sm" src={profilePic} />
                )}
                <Flexbox flexDirection="column" rowGap={0.5}>
                  <Text variant='bodySm700'>{fullName}</Text>
                  {email && <Text variant='captionReg500'>{email}</Text>}
                </Flexbox>
              </Flexbox>
            ),
          },
          ...conditionalArray(!isOnboarding, [
            {
              textContent: () => (
                <Flexbox alignItems="center">
                  <FontAwesomeIcon color="default" icon={faCircleUser} />
                  <Text ml={1} variant='bodySm400'>
                    Account Settings
                  </Text>
                </Flexbox>
              ),
              href: ACCOUNT_SETTINGS_ACCOUNT_ACCESS,
            },
          ]),
          ...conditionalArray(features?.whoami, [
            {
              textContent: () => (
                <Flexbox alignItems="center">
                  <FontAwesomeIcon color="default" icon={faBug} />
                  <Text ml={1} variant='bodySm400'>
                    Whoami? (Debug Info)
                  </Text>
                </Flexbox>
              ),
              onClick: () => showModal('WHOAMI_MODAL'),
            },
          ]),
          ...conditionalArray(features?.controlPanel, [
            {
              textContent: () => (
                <Flexbox alignItems="center">
                  <FontAwesomeIcon color="default" icon={faBug} />
                  <Text ml={1} variant='bodySm400'>
                    Control Panel
                  </Text>
                </Flexbox>
              ),
              onClick: () => showModal('SUPERADMIN_CONTROL_PANEL_MODAL'),
            },
          ]),
          ...conditionalArray(showAccessibilityWidgetMenuItem, [
            {
              textContent: () => (
                <Flexbox alignItems="center">
                  <FontAwesomeIcon color="default" icon={faPerson} />
                  <Text ml={1} variant='bodySm400'>
                    {showAccessibilityWidget
                      ? 'Hide accessibility widget'
                      : 'Show accessibility widget'}
                  </Text>
                </Flexbox>
              ),
              onClick: handleAccessibilityWidgetItemClick,
            },
          ]),
          ...conditionalArray(!isHESchoolAffiliation, [
            {
              textContent: () => (
                <Flexbox alignItems="center">
                  <FontAwesomeIcon color="default" icon={faLanguage} />
                  <Text ml={1} variant='bodySm400'>
                    {showGoogleTranslateDropdown && isDesktop
                      ? 'Hide Language Translation'
                      : 'Language Translation'}
                  </Text>
                </Flexbox>
              ),
              onClick: handleToggleLanguageTranslation,
            },
          ]),
          {
            textContent: () => (
              <Flexbox alignItems="center">
                <FontAwesomeIcon color="default" icon={faSitemap} />
                <Text ml={1} variant='bodySm400'>
                  Sitemap
                </Text>
              </Flexbox>
            ),
            href: SITEMAP,
          },
          ...conditionalArray(isMember, [
            {
              textContent: () => (
                <Flexbox alignItems="center">
                  <FontAwesomeIcon color="default" icon={faUsers} />
                  <Text ml={1} variant='bodySm400'>
                    Manage Affiliations
                  </Text>
                </Flexbox>
              ),
              href: ACCOUNT_SETTINGS_AFFILIATIONS,
            },
          ]),
          ...conditionalArray(!(isDemoStudent && isOnboarding), [
            {
              textContent: () => (
                <Flexbox alignItems="center">
                  <FontAwesomeIcon
                    color="default"
                    icon={faArrowRightFromBracket}
                  />
                  <Text ml={1} variant='bodySm400'>
                    Logout
                  </Text>
                </Flexbox>
              ),
              href: '/logout',
            },
          ]),
        ],
      }}
    />
  );
};

Settings.propTypes = {
  toggleGoogleTranslate: func.isRequired,
  user: shape({
    isCounselor: bool,
    isHESchoolAffiliation: bool,
    isMember: bool,
    isDemoStudent: bool,
  }).isRequired,
  isOnboarding: bool.isRequired,
  showGoogleTranslate: bool.isRequired,
};

export default Settings;
